import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, LocalizedDateInput, SearchableSelect, Spinner } from '../../../shared/components';
import { useAssetsModuleVisible } from '../../assets/assetsModule';
import { getAssets } from '../../assets/services/assetService';
import { Asset } from '../../assets/types/Asset';
import { getContacts } from '../../contacts/services/contactService';
import { Contact } from '../../contacts/types/Contact';
import { getProducts } from '../../products/services/productService';
import { Product } from '../../products/types/Product';
import { getServices } from '../../services/services/serviceCatalogService';
import { ServiceOffering } from '../../services/types/Service';
import { createEmptyLine, formatMoney, FALLBACK_CURRENCY, linesToRequest, parseNumber, pickDefaultLocationId, previewInvoiceTotals, toIsoDate, applyContactToBillTo, billToCreatePayload, emptyBillTo } from '../invoiceForm';
import { createInvoice, getInvoiceSettings } from '../services/invoiceService';
import { rememberTenantCurrency } from '../tenantCurrency';
import { InvoiceBillToFields } from './InvoiceBillToFields';
import { InvoiceLinesEditor } from './InvoiceLinesEditor';
import catalog from '../../catalog/Catalog.module.css';
import { listLocations } from '../../companies';
import { Location } from '../../companies/types/Company';
import styles from '../Invoice.module.css';

export const AddInvoice: React.FC = () => {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();
  const isAssetsModuleVisible = useAssetsModuleVisible();
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [assets, setAssets] = React.useState<Asset[]>([]);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [services, setServices] = React.useState<ServiceOffering[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [contactId, setContactId] = React.useState('');
  const [assetId, setAssetId] = React.useState('');
  const [locationId, setLocationId] = React.useState('');
  const [locations, setLocations] = React.useState<Location[]>([]);
  const [dueDate, setDueDate] = React.useState('');
  const [customerNote, setCustomerNote] = React.useState('');
  const [internalNote, setInternalNote] = React.useState('');
  const [pricesIncludeTax, setPricesIncludeTax] = React.useState(false);
  const [currency, setCurrency] = React.useState(FALLBACK_CURRENCY);
  const [billTo, setBillTo] = React.useState(emptyBillTo());
  const [lines, setLines] = React.useState([createEmptyLine()]);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [contactData, productData, serviceData, assetData, settings, locationData] = await Promise.all([
          getContacts({ pageSize: 200 }),
          getProducts({ page: 1, pageSize: 200, status: 'active' }),
          getServices({ page: 1, pageSize: 200, status: 'active' }),
          isAssetsModuleVisible ? getAssets({ page: 1, pageSize: 200 }) : Promise.resolve([]),
          getInvoiceSettings(),
          listLocations({ isActive: true, pageSize: 200 }).catch(() => [] as Location[]),
        ]);
        setContacts(contactData);
        setProducts(productData);
        setServices(serviceData);
        setAssets(assetData);
        setLocations(locationData);
        setLocationId(pickDefaultLocationId(locationData));
        setPricesIncludeTax(settings.pricesIncludeTax);
        setCurrency(settings.defaultCurrency);
        rememberTenantCurrency(settings.defaultCurrency);
      } catch {
        setGeneralError(t('failedToLoad', { ns: 'invoices' }));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [isAssetsModuleVisible, t]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!contactId) nextErrors.contactId = t('contactRequired', { ns: 'invoices' });
    const validLines = linesToRequest(lines).filter(line => line.quantity > 0);
    if (validLines.length === 0) nextErrors.lines = t('lineRequired', { ns: 'invoices' });
    lines.forEach(line => {
      if (line.lineType === 'custom' && !line.name.trim() && (parseNumber(line.quantity) || parseNumber(line.unitPrice))) {
        nextErrors.lines = t('customNameRequired', { ns: 'invoices' });
      }
      if (line.lineType === 'product' && !line.productId && parseNumber(line.quantity)) {
        nextErrors.lines = t('catalogRequired', { ns: 'invoices' });
      }
      if (line.lineType === 'service' && !line.serviceOfferingId && parseNumber(line.quantity)) {
        nextErrors.lines = t('catalogRequired', { ns: 'invoices' });
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setGeneralError(null);
    try {
      const created = await createInvoice({
        contactId,
        assetId: assetId || undefined,
        locationId: locationId || undefined,
        dueDate: toIsoDate(dueDate),
        ...billToCreatePayload(billTo),
        customerNote: customerNote.trim() || undefined,
        internalNote: internalNote.trim() || undefined,
        pricesIncludeTax,
        lines: linesToRequest(lines).filter(line => line.quantity > 0),
      });
      if (!created) {
        setGeneralError(t('failedToSave', { ns: 'invoices' }));
        return;
      }
      navigate(`/invoices/${created.id}`);
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSave', { ns: 'invoices' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loading', { ns: 'invoices' })} /></div>;
  }

  const summary = previewInvoiceTotals(lines, pricesIncludeTax);

  return (
    <div className={`${catalog.page} ${styles.page}`}>
      <Breadcrumb items={[
        { label: t('title', { ns: 'invoices' }), path: '/invoices' },
        { label: t('addTitle', { ns: 'invoices' }), path: '/invoices/add' },
      ]} />
      <h2 className={catalog.title}>{t('addTitle', { ns: 'invoices' })}</h2>
      <p className={styles.hint}>{t('draftHint', { ns: 'invoices' })}</p>
      {generalError ? <div className="error">{generalError}</div> : null}

      <form onSubmit={onSubmit}>
        <div className={styles.workspace}>
          <div className={styles.mainColumn}>
            <div className={styles.metaBand}>
              <div className={catalog.field}>
                <label className={catalog.label}>{t('customer', { ns: 'invoices' })}</label>
                <SearchableSelect
                  value={contactId}
                  onChange={nextContactId => {
                    setContactId(nextContactId);
                    const contact = contacts.find(item => item.id === nextContactId);
                    if (contact) setBillTo(previous => applyContactToBillTo(previous, contact));
                  }}
                  options={contacts.map(contact => ({ value: contact.id, label: contact.name, meta: contact.email }))}
                  placeholder={t('selectContact', { ns: 'invoices' })}
                  inputClassName={catalog.input}
                />
                {errors.contactId ? <div className={catalog.fieldError}>{errors.contactId}</div> : null}
              </div>
              {isAssetsModuleVisible ? (
                <div className={catalog.field}>
                  <label className={catalog.label}>{t('asset', { ns: 'invoices' })}</label>
                  <SearchableSelect
                    value={assetId}
                    onChange={setAssetId}
                    options={assets.map(asset => ({ value: asset.id, label: asset.name, meta: asset.identifier }))}
                    placeholder={t('selectAsset', { ns: 'invoices' })}
                    clearOption={{ value: '', label: t('noAsset', { ns: 'invoices' }) }}
                    inputClassName={catalog.input}
                  />
                </div>
              ) : null}
              {locations.length > 0 ? (
                <div className={catalog.field}>
                  <label className={catalog.label}>{t('location', { ns: 'invoices' })}</label>
                  <select className={catalog.input} value={locationId} onChange={event => setLocationId(event.target.value)}>
                    <option value="">{t('noLocation', { ns: 'invoices' })}</option>
                    {locations.map(location => (
                      <option key={location.id} value={location.id}>{location.name}</option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className={catalog.field}>
                <label className={catalog.label}>{t('dueDate', { ns: 'invoices' })}</label>
                <LocalizedDateInput value={dueDate} onValueChange={setDueDate} emptyLabel={t('dueDate', { ns: 'invoices' })} />
              </div>
              <div className={catalog.field}>
                <label className={catalog.label}>{t('pricesIncludeTax', { ns: 'invoices' })}</label>
                <label className={styles.checkboxRow}>
                  <input type="checkbox" checked={pricesIncludeTax} onChange={event => setPricesIncludeTax(event.target.checked)} />
                  {t('pricesIncludeTax', { ns: 'invoices' })}
                </label>
              </div>
            </div>

            <div className={catalog.headerRow}>
              <h3 className={styles.sectionTitle}>{t('lines', { ns: 'invoices' })}</h3>
              <button className="btn btn-secondary" type="button" onClick={() => setLines(prev => [...prev, createEmptyLine()])}>
                {t('addLine', { ns: 'invoices' })}
              </button>
            </div>
            {errors.lines ? <div className={catalog.fieldError}>{errors.lines}</div> : null}
            <InvoiceLinesEditor
              lines={lines}
              pricesIncludeTax={pricesIncludeTax}
              products={products}
              services={services}
              onChange={setLines}
            />

            <div>
              <p className={styles.hint}>{t('billToHint', { ns: 'invoices' })}</p>
              <InvoiceBillToFields value={billTo} onChange={setBillTo} />
            </div>
            <div className={catalog.field}>
              <label className={catalog.label}>{t('customerNote', { ns: 'invoices' })}</label>
              <textarea className={catalog.textarea} value={customerNote} onChange={event => setCustomerNote(event.target.value)} />
            </div>
            <div className={catalog.field}>
              <label className={catalog.label}>{t('internalNote', { ns: 'invoices' })}</label>
              <textarea className={catalog.textarea} value={internalNote} onChange={event => setInternalNote(event.target.value)} />
            </div>
          </div>

          <aside className={styles.summaryAside}>
            <h3 className={styles.summaryTitle}>{t('summary', { ns: 'invoices' })}</h3>
            <dl>
              <div className={styles.summaryRow}>
                <dt>{t('subtotal', { ns: 'invoices' })}</dt>
                <dd>{formatMoney(summary.subtotal, currency)}</dd>
              </div>
              <div className={styles.summaryRow}>
                <dt>{t('taxTotal', { ns: 'invoices' })}</dt>
                <dd>{formatMoney(summary.taxTotal, currency)}</dd>
              </div>
              <div className={`${styles.summaryRow} ${styles.summaryRowStrong}`}>
                <dt>{t('total', { ns: 'invoices' })}</dt>
                <dd>{formatMoney(summary.total, currency)}</dd>
              </div>
            </dl>
            <div className={styles.summaryActions}>
              <button className="btn btn-primary" type="submit" disabled={saving}>{t('save', { ns: 'invoices' })}</button>
              <button className="btn btn-secondary" type="button" onClick={() => navigate('/invoices')}>{t('back', { ns: 'invoices' })}</button>
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
};
